import { useEffect, lazy, Suspense } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { GlobalProvider } from '@/contexts/GlobalProvider';
import AnnouncementPopup from '@/components/ui/AnnouncementPopup';
import WechatBrowserGuide from '@/components/ui/WechatBrowserGuide';
import { useGlobalErrorHandler } from '@/hooks/useGlobalErrorHandler';
import { setGlobalNavigate } from '@/hooks/useRouter';
import Loading from '@/components/ui/Loading';
import Layout from './Layout';

// 核心页面（首屏加载）
import IndexPage from '@/pages/index/+Page';
import LoginPage from '@/pages/login/+Page';
import RegisterPage from '@/pages/register/+Page';
import ErrorPage from '@/pages/_error/+Page';
import RoomClosedPage from '@/pages/room-closed/+Page';

// 懒加载页面
const ProfilePage = lazy(() => import('@/pages/profile/+Page'));
const WalletPage = lazy(() => import('@/pages/wallet/+Page'));
const RechargePage = lazy(() => import('@/pages/recharge/+Page'));
const RechargeAutoPage = lazy(() => import('@/pages/recharge/auto/+Page'));
const RechargeFormPage = lazy(() => import('@/pages/recharge/form/+Page'));
const RechargeManualPage = lazy(() => import('@/pages/recharge/manual/+Page'));
const WithdrawPage = lazy(() => import('@/pages/withdraw/+Page'));
const WithdrawManualPage = lazy(() => import('@/pages/withdraw/manual/+Page'));
const WithdrawManualRecordsPage = lazy(() => import('@/pages/withdraw/manual/records/+Page'));
const TransferPage = lazy(() => import('@/pages/transfer/+Page'));
const TransferRecordsPage = lazy(() => import('@/pages/transfer-records/+Page'));
const BillPage = lazy(() => import('@/pages/bill/+Page'));
const OrdersPage = lazy(() => import('@/pages/orders/+Page'));
const OrderDetailPage = lazy(() => import('@/pages/orders/@orderId/+Page'));
const PayoutOrdersPage = lazy(() => import('@/pages/payout-orders/+Page'));
const RoomsPage = lazy(() => import('@/pages/rooms/+Page'));
const ChatroomPage = lazy(() => import('@/pages/chatroom/+Page'));
const ChatroomWithIdPage = lazy(() => import('@/pages/chatroom/@roomId/+Page'));
const BetRecordsPage = lazy(() => import('@/pages/bet-records/+Page'));
const BetStatsPage = lazy(() => import('@/pages/bet-stats/+Page'));
const InvitePage = lazy(() => import('@/pages/invite/+Page'));
const CheckinPage = lazy(() => import('@/pages/checkin/+Page'));
const ActivityPage = lazy(() => import('@/pages/activity/+Page'));
const SupportPage = lazy(() => import('@/pages/support/+Page'));
const CustomerServicePage = lazy(() => import('@/pages/customer-service/+Page'));
const AboutPage = lazy(() => import('@/pages/about/+Page'));
const RulesPage = lazy(() => import('@/pages/rules/@roomId/+Page'));
const TrendPage = lazy(() => import('@/pages/trend/@roomId/+Page'));
const DynamicPage = lazy(() => import('@/pages/page/@key/+Page'));
const ChangePasswordPage = lazy(() => import('@/pages/change-password/+Page'));
const PgLauncherPage = lazy(() => import('@/pages/pg-launcher/+Page'));
const PgLobbyPage = lazy(() => import('@/pages/pg-lobby/+Page'));
const PgVerificationPage = lazy(() => import('@/pages/pg-verification/+Page'));
const PgBetVerificationPage = lazy(() => import('@/pages/pg-bet-verification/+Page'));
const PgTournamentPage = lazy(() => import('@/pages/pg-tournament/+Page'));
const PgBetHistoryPage = lazy(() => import('@/pages/pg-bet-history/+Page'));
const CqLauncherPage = lazy(() => import('@/pages/cq-launcher/+Page'));
const CqLobbyPage = lazy(() => import('@/pages/cq-lobby/+Page'));
const FgLauncherPage = lazy(() => import('@/pages/fg-launcher/+Page'));
const BgLauncherPage = lazy(() => import('@/pages/bg-launcher/+Page'));
const CqBetHistoryPage = lazy(() => import('@/pages/cq-bet-history/+Page'));
const FgBetHistoryPage = lazy(() => import('@/pages/fg-bet-history/+Page'));
const BgBetHistoryPage = lazy(() => import('@/pages/bg-bet-history/+Page'));
const FgLobbyPage = lazy(() => import('@/pages/fg-lobby/+Page'));
const AgentCenterPage = lazy(() => import('@/pages/agent-center/+Page'));
const AgentCenterTeamPage = lazy(() => import('@/pages/agent-center/team/+Page'));
const AgentCenterEarningsPage = lazy(() => import('@/pages/agent-center/earnings/+Page'));
const RebateRecordsPage = lazy(() => import('@/pages/rebate-records/+Page'));

/**
 * 全局错误处理器组件
 */
function GlobalErrorHandler({ children }: { children: React.ReactNode }) {
  useGlobalErrorHandler();
  return <>{children}</>;
}

/**
 * 注册全局导航函数
 */
function NavigateProvider({ children }: { children: React.ReactNode }) {
  const nav = useNavigate();

  useEffect(() => {
    setGlobalNavigate((to, options) => {
      const replace = options?.replace || options?.overwriteLastHistoryEntry || false;
      nav(to, { replace });
    });
  }, [nav]);

  return <>{children}</>;
}

/**
 * 懒加载包装器
 */
function LazyPage({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={<Loading fullscreen text="加载中..." />}>
      {children}
    </Suspense>
  );
}

export default function App() {
  return (
    <NavigateProvider>
      <GlobalProvider>
        <GlobalErrorHandler>
          <Layout>
            <Routes>
              {/* 首页（首屏加载） */}
              <Route path="/" element={<IndexPage />} />

              {/* 认证（首屏加载） */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/change-password" element={<LazyPage><ChangePasswordPage /></LazyPage>} />

              {/* 用户中心 */}
              <Route path="/profile" element={<LazyPage><ProfilePage /></LazyPage>} />
              <Route path="/wallet" element={<LazyPage><WalletPage /></LazyPage>} />

              {/* 充值 */}
              <Route path="/recharge" element={<LazyPage><RechargePage /></LazyPage>} />
              <Route path="/recharge/auto" element={<LazyPage><RechargeAutoPage /></LazyPage>} />
              <Route path="/recharge/form" element={<LazyPage><RechargeFormPage /></LazyPage>} />
              <Route path="/recharge/manual" element={<LazyPage><RechargeManualPage /></LazyPage>} />

              {/* 提现 */}
              <Route path="/withdraw" element={<LazyPage><WithdrawPage /></LazyPage>} />
              <Route path="/withdraw/manual" element={<LazyPage><WithdrawManualPage /></LazyPage>} />
              <Route path="/withdraw/manual/records" element={<LazyPage><WithdrawManualRecordsPage /></LazyPage>} />

              {/* 转账 */}
              <Route path="/transfer" element={<LazyPage><TransferPage /></LazyPage>} />
              <Route path="/transfer-records" element={<LazyPage><TransferRecordsPage /></LazyPage>} />

              {/* 账单和订单 */}
              <Route path="/bill" element={<LazyPage><BillPage /></LazyPage>} />
              <Route path="/orders" element={<LazyPage><OrdersPage /></LazyPage>} />
              <Route path="/orders/:orderId" element={<LazyPage><OrderDetailPage /></LazyPage>} />
              <Route path="/payout-orders" element={<LazyPage><PayoutOrdersPage /></LazyPage>} />

              {/* 游戏相关 */}
              <Route path="/rooms" element={<LazyPage><RoomsPage /></LazyPage>} />
              <Route path="/chatroom" element={<LazyPage><ChatroomPage /></LazyPage>} />
              <Route path="/chatroom/:roomId" element={<LazyPage><ChatroomWithIdPage /></LazyPage>} />
              <Route path="/bet-records" element={<LazyPage><BetRecordsPage /></LazyPage>} />
              <Route path="/bet-stats" element={<LazyPage><BetStatsPage /></LazyPage>} />
              <Route path="/rules/:roomId" element={<LazyPage><RulesPage /></LazyPage>} />
              <Route path="/trend/:roomId" element={<LazyPage><TrendPage /></LazyPage>} />

              {/* 活动和推广 */}
              <Route path="/invite" element={<LazyPage><InvitePage /></LazyPage>} />
              <Route path="/checkin" element={<LazyPage><CheckinPage /></LazyPage>} />
              <Route path="/activity" element={<LazyPage><ActivityPage /></LazyPage>} />

              {/* 代理中心 */}
              <Route path="/agent-center" element={<LazyPage><AgentCenterPage /></LazyPage>} />
              <Route path="/agent-center/team" element={<LazyPage><AgentCenterTeamPage /></LazyPage>} />
              <Route path="/agent-center/earnings" element={<LazyPage><AgentCenterEarningsPage /></LazyPage>} />
              <Route path="/rebate-records" element={<LazyPage><RebateRecordsPage /></LazyPage>} />

              {/* 帮助 */}
              <Route path="/support" element={<LazyPage><SupportPage /></LazyPage>} />
              <Route path="/customer-service" element={<LazyPage><CustomerServicePage /></LazyPage>} />
              <Route path="/about" element={<LazyPage><AboutPage /></LazyPage>} />

              {/* PG 游戏 */}
              <Route path="/pg-lobby" element={<LazyPage><PgLobbyPage /></LazyPage>} />
              <Route path="/pg-launcher" element={<LazyPage><PgLauncherPage /></LazyPage>} />
              <Route path="/pg-verification" element={<LazyPage><PgVerificationPage /></LazyPage>} />
              <Route path="/pg-bet-verification" element={<LazyPage><PgBetVerificationPage /></LazyPage>} />
              <Route path="/pg-tournament" element={<LazyPage><PgTournamentPage /></LazyPage>} />
              <Route path="/pg-bet-history" element={<LazyPage><PgBetHistoryPage /></LazyPage>} />

              {/* CQ 游戏 */}
              <Route path="/cq-lobby" element={<LazyPage><CqLobbyPage /></LazyPage>} />
              <Route path="/cq-launcher" element={<LazyPage><CqLauncherPage /></LazyPage>} />
              <Route path="/cq-bet-history" element={<LazyPage><CqBetHistoryPage /></LazyPage>} />

              {/* 5G 游戏 */}
              <Route path="/fg-launcher" element={<LazyPage><FgLauncherPage /></LazyPage>} />
              <Route path="/fg-bet-history" element={<LazyPage><FgBetHistoryPage /></LazyPage>} />

              <Route path="/fg-lobby" element={<LazyPage><FgLobbyPage /></LazyPage>} />

              {/* BG 游戏 */}
              <Route path="/bg-launcher" element={<LazyPage><BgLauncherPage /></LazyPage>} />
              <Route path="/bg-bet-history" element={<LazyPage><BgBetHistoryPage /></LazyPage>} />

              {/* 动态页面 */}
              <Route path="/page/:key" element={<LazyPage><DynamicPage /></LazyPage>} />

              {/* 错误页面（首屏加载） */}
              <Route path="/room-closed" element={<RoomClosedPage />} />
              <Route path="*" element={<ErrorPage />} />
            </Routes>
          </Layout>

          {/* 全局弹窗 */}
          <AnnouncementPopup />
          <WechatBrowserGuide />
        </GlobalErrorHandler>
      </GlobalProvider>
    </NavigateProvider>
  );
}
